import { JOURNEY_LIBRARY, JOURNEY_LIBRARY_BY_ID, JOURNEY_LIBRARY_CATEGORIES } from './journeyLibrary.js';
import { EASY_READ_JOURNEY_SUMMARIES } from './easyReadJourneySummaryCopy.js';
import { createGenericJourney } from './genericJourneyRuntime.js';
import { getHospitalJourneyByAccessKey } from './hospitalJourneys.js';
import { EASY_READ_JOURNEYS } from './easyReadJourneys.js';
import { WAITING_FOR_SURGERY_TEMPLATE, WAITING_FOR_SURGERY_ID, createWaitingForSurgeryJourney } from './waitingForSurgeryJourney.js';

export const EASY_READ_CONFIGURABLE_JOURNEYS = Object.freeze([
  {
    id: 'easy-read-day-surgery',
    title: 'Day Surgery: Easy Read version',
    summary: EASY_READ_JOURNEY_SUMMARIES['easy-read-day-surgery'],
    category: 'easy_read',
    accessKey: 'kch-ld-ds-4N7P2Q8R',
    route: '/journeys/easy-read-day-surgery',
  },
  {
    id: 'easy-read-outpatient-appointment',
    title: 'Outpatient Appointment: Easy Read version',
    summary: EASY_READ_JOURNEY_SUMMARIES['easy-read-outpatient-appointment'],
    category: 'easy_read',
    accessKey: 'er-opd-7M4Q2V9K',
    route: '/journeys/easy-read-outpatient-appointment',
  },
  {
    id: 'easy-read-scan',
    title: 'Scan: Easy Read version',
    summary: EASY_READ_JOURNEY_SUMMARIES['easy-read-scan'],
    category: 'easy_read',
    accessKey: 'er-scan-5R8N3C6T',
    route: '/journeys/easy-read-scan',
  },
  {
    id: 'easy-read-blood-test',
    title: 'Blood Test: Easy Read version',
    summary: EASY_READ_JOURNEY_SUMMARIES['easy-read-blood-test'],
    category: 'easy_read',
    accessKey: 'er-blood-2H7W5P9F',
    route: '/journeys/easy-read-blood-test',
  },
  {
    id: 'easy-read-emergency-department',
    title: 'Emergency Department: Easy Read version',
    summary: EASY_READ_JOURNEY_SUMMARIES['easy-read-emergency-department'],
    category: 'easy_read',
    accessKey: 'er-ed-8D3K6R2N',
    route: '/journeys/easy-read-emergency-department',
  },
  {
    id: 'easy-read-hospital-stay',
    title: 'Hospital Stay: Easy Read version',
    summary: EASY_READ_JOURNEY_SUMMARIES['easy-read-hospital-stay'],
    category: 'easy_read',
    accessKey: 'er-ward-4V9C2M7Q',
    route: '/journeys/easy-read-hospital-stay',
  },
]);

export const CONFIGURABLE_JOURNEY_CATEGORIES = Object.freeze({
  ...JOURNEY_LIBRARY_CATEGORIES,
  easy_read: 'Easy Read Journeys',
  waiting_well: 'Unlisted pilot journeys',
});

export const CONFIGURABLE_JOURNEY_LIBRARY = Object.freeze([
  ...JOURNEY_LIBRARY,
  ...EASY_READ_CONFIGURABLE_JOURNEYS,
  WAITING_FOR_SURGERY_TEMPLATE,
]);

export const CONFIGURABLE_JOURNEY_BY_ID = new Map(
  CONFIGURABLE_JOURNEY_LIBRARY.map((journey) => [journey.id, journey]),
);

const EASY_READ_BY_ACCESS_KEY = new Map(
  EASY_READ_CONFIGURABLE_JOURNEYS.map((journey) => [journey.accessKey, journey]),
);

const EASY_READ_BY_SOURCE_ID = new Map(
  EASY_READ_CONFIGURABLE_JOURNEYS
    .map((journey) => {
      const source = getHospitalJourneyByAccessKey(journey.accessKey);
      return source?.id ? [source.id, journey] : null;
    })
    .filter(Boolean),
);

function cloneJourney(journey) {
  return journey ? structuredClone(journey) : null;
}

export function createConfigurableJourney(journeyId) {
  if (journeyId === WAITING_FOR_SURGERY_ID) return createWaitingForSurgeryJourney();
  if (JOURNEY_LIBRARY_BY_ID[journeyId]) return createGenericJourney(journeyId);
  const template = CONFIGURABLE_JOURNEY_BY_ID.get(journeyId);
  if (!template?.accessKey) return null;
  const source = cloneJourney(getHospitalJourneyByAccessKey(template.accessKey));
  if (!source) return null;
  return {
    ...source,
    trustId: 'generic_nhs',
    trustName: 'NHS hospital',
    hospitalName: source.hospitalName || template.title.replace(': Easy Read version', ''),
    status: 'draft',
    public: false,
    templateId: template.id,
  };
}

export function configurableJourneyRoute(journeyOrId) {
  const id = typeof journeyOrId === 'string' ? journeyOrId : journeyOrId?.id;
  const template = CONFIGURABLE_JOURNEY_BY_ID.get(id);
  return template?.route || (id ? `/journeys/${encodeURIComponent(id)}` : '/JourneyInfo');
}

export function getEasyReadConfigurableJourney(reference) {
  return EASY_READ_BY_ACCESS_KEY.get(reference)
    || EASY_READ_BY_SOURCE_ID.get(reference)
    || EASY_READ_CONFIGURABLE_JOURNEYS.find((journey) => journey.id === reference)
    || null;
}

export function isEasyReadConfigurableJourney(reference) {
  return Boolean(getEasyReadConfigurableJourney(reference));
}

export function getEasyReadJourneySource(reference) {
  const template = getEasyReadConfigurableJourney(reference);
  return template ? getHospitalJourneyByAccessKey(template.accessKey) : null;
}

export function easyReadJourneyCount() {
  return Object.keys(EASY_READ_JOURNEYS).length + 1;
}
